import PropTypes from 'prop-types';
import './Form.css';

/**
 * Dropdown select primitive.
 *
 * Visual parity with `Input`: same border, padding, focus ring, plus a
 * built-in chevron adornment (rendered via background-image so the native
 * `<select>` semantics are preserved).
 *
 * @param {object}  props
 * @param {boolean} [props.disabled=false]
 * @param {boolean|string} [props.error=false]
 *   When truthy, applies `.error` class and sets `aria-invalid="true"`.
 * @param {string}  [props.placeholder]
 *   Renders a disabled, non-selectable first `<option>` (the standard
 *   accessible pattern for required selects). When provided and neither
 *   `value` nor `defaultValue` is set, the placeholder is selected
 *   initially.
 * @param {string}  [props.className='']
 *   Concatenated last so consumer classes win specificity ties.
 * @param {React.ReactNode} props.children  `<option>` elements.
 * @param {...*}    props  Any other `<select>` attribute is forwarded.
 *
 * @example
 *   <InputRow label="Tribunal">
 *     <Select placeholder="Seleccionar tribunal…">
 *       <option>2° Juzgado Civil de Santiago</option>
 *       <option>15° Juzgado Civil de Santiago</option>
 *     </Select>
 *   </InputRow>
 */
export function Select({
  disabled = false,
  error = false,
  placeholder,
  className = '',
  children,
  value,
  defaultValue,
  ...props
}) {
  const cls = ['form-input', 'form-select', error ? 'error' : '', className]
    .filter(Boolean)
    .join(' ');

  // When a placeholder is provided, the underlying value defaults to "" so
  // the disabled placeholder option is shown until the user picks a value.
  const placeholderValue = '';
  const resolvedDefault =
    placeholder && value === undefined && defaultValue === undefined
      ? placeholderValue
      : defaultValue;

  return (
    <select
      className={cls}
      disabled={disabled}
      aria-invalid={error ? true : props['aria-invalid']}
      value={value}
      defaultValue={resolvedDefault}
      {...props}
    >
      {placeholder ? (
        <option value={placeholderValue} disabled hidden>
          {placeholder}
        </option>
      ) : null}
      {children}
    </select>
  );
}

// InputRow labels the first element marked as a form control.
Select.formControl = true;

Select.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Renders a disabled first option used as a placeholder. */
  placeholder: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
};

export default Select;
